import type { ComboboxItem, RadioProps } from "@mantine/core";
import { DateTime } from "luxon";
import {
  FoerderRegime,
  GeneratorType,
  MieterstromzuschlagApplicationIntent
} from "../../../types/api.types";
import { MIETERSTROMZUSCHLAG_FORM } from "../../../utils/constants";
import type { FormFieldData } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { getFoerderregimeLabel } from "../../Paragraph6/utils/getFoerderregimeLabel";
import type { GeneratorSingleEndpointResponse } from "./GeneratorModal.types";

// Can be removed later once the Veraeusserungsformen work on production.
// Left for now (April 2026) in case we need a rollback
export const isVeraeusserungsformenReady = true;

export const GENERATOR_EDIT_TABS = {
  TAB_GENERAL: "general",
  TAB_EEG_ANLAGE_DATA: "eeg-anlage",
  TAB_KWK_ANLAGE_DATA: "kwk-anlage",
  TAB_STROMSTEUER_DATA: "stromsteuerliche-anlage",
  TAB_PRICE_DATA: "preisdaten",
  TAB_HISTORY: "historie"
};

const GENERATOR_FORM_DATA = {
  name: {
    name: "name",
    required: true,
    label: "Bezeichnung",
    helpText: "Frei wählbar, wird nur zur Identifikation verwendet",
    type: "text",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  type: {
    name: "type",
    type: "select",
    required: true,
    disabled: true,
    label: "Typ",
    data: [
      { value: GeneratorType.EEGAnlageSolar, label: "Photovoltaik" },
      {
        value: GeneratorType.EEGAnlageBiomasse,
        label: "Biomasse (fest, flüssig, gasförmig) - EEG"
      },
      { value: GeneratorType.EEGAnlageWind, label: "Windkraft" },
      { value: GeneratorType.EEGAnlageWasser, label: "Wasserkraft" },
      {
        value: GeneratorType.EEGAnlageGSGK,
        label: "Geothermie, Solarthermie, Grubengas, Klärschlamm (GSGK) - EEG"
      },
      {
        value: GeneratorType.KWKAnlageVerbrennung,
        label: "KWK fossil (u. a. Erdgas-BHKW)"
      },
      {
        value: GeneratorType.KWKAnlageBiomasse,
        label: "Biomasse - nicht EEG"
      },
      {
        value: GeneratorType.KWKAnlageGSGK,
        label:
          "Geothermie, Solarthermie, Grubengas, Klärschlamm (GSGK) - nicht EEG"
      },
      { value: GeneratorType.Sonstige, label: "Sonstige" }
    ] satisfies Array<ComboboxItem>,
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  operation_periods: {
    name: "operation_periods",
    type: "custom",
    required: true,
    label: "Betreiber",
    helpText:
      "Hier bitte das Unternehmen eintragen, welches Betreiber der Stromerzeugungsanlage ist",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  connection: {
    name: "connection",
    type: "select",
    required: true,
    label: "Netzverknüpfungspunkt",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  installed_capacity: {
    name: "installed_capacity",
    type: "number",
    required: true,
    label: "Nettonennleistung in kW",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  commissioning: {
    name: "commissioning",
    type: "date",
    required: true,
    label: "Inbetriebnahme",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  decommissioning: {
    name: "decommissioning",
    type: "date",
    required: false,
    label: "Außerbetriebnahme",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },

  // price data
  feedin_tariff_deprecated: {
    name: "feedin_tariff_deprecated",
    type: "number",
    required: false,
    disabled: true,
    label: "(veraltet) Einspeisevergütung [ct/kWh]",
    helpText:
      "**Diese Angabe wird nicht mehr unterstützt. Verwenden Sie für PV-Anlagen stattdessen die \nEingabefelder 'Anzulegender Wert', 'Direktvermarktungserlös' & 'Direktvermarktungsentgelt'**\n\n\nUm ein wirtschaftlichlich optimales Mess- und Bilanzierungskonzept erstellen zu können, \nist die Höhe der Vergütung, die für ins öffentliche Netz eingespeisten Strom gezahlt wird, \nvon Bedeutung.\n\n\nEinzutragen ist die Vergütung, die für eine kWh in das öffentliche Netz eingespeisten \nStrom effektiv gezahlt wird.\n\n\nFür *EEG-Anlagen* ist dies die fixe EEG-Vergütung bzw. die Gesamtvergütung im Rahmen der \nDirektvermarktung (Summe aus Direktvermarktungserlösen und Marktprämie).\n\n\nFür *KWK-Anlagen* setzt sich die Einspeisevergütung zusammen aus der Vergütung des \nStroms (Direktvermarktung oder üblicher Preis) sowie ggf. KWKG-Zuschlagszahlungen und \nvermiedenen Netzentgelten.",
    hiddenIfNull: true,
    group: GENERATOR_EDIT_TABS.TAB_PRICE_DATA
  },
  feedin_tariff_non_pv: {
    name: "feedin_tariff_non_pv",
    type: "number",
    label: "Einspeisevergütung [ct/kWh]",
    helpText:
      "\nEinzutragen ist die Vergütung, die für eine kWh in das öffentliche Netz eingespeisten\nStrom effektiv gezahlt wird.\n\n\nFür *EEG-Anlagen* ist dies die fixe EEG-Vergütung bzw. die Gesamtvergütung im Rahmen der\nDirektvermarktung (Summe aus Direktvermarktungserlösen und Marktprämie).\n\n\nFür *KWK-Anlagen* setzt sich die Einspeisevergütung zusammen aus der Vergütung des\nStroms (Direktvermarktung oder üblicher Preis) sowie ggf. KWKG-Zuschlagszahlungen und\nvermiedenen Netzentgelten.\n",
    group: GENERATOR_EDIT_TABS.TAB_PRICE_DATA
  },
  applicable_value_pv: {
    name: "applicable_value_pv",
    type: "number",
    label: "Anzulegender Wert [ct/kWh]",
    decimalScale: 5,
    helpText:
      "\nFür PV-Anlagen, die eine Einspeisevergütung oder Marktprämie erhalten wird der anzulegende Wert\ndurch die BNetzA festgelegt.\n\n\nFür PV-Anlagen, die an einer Ausschreibung teilnehmen (i.d.R. >750kWp), ist der bezuschlagte\nGebotswert einzutragen.\n",
    group: GENERATOR_EDIT_TABS.TAB_PRICE_DATA
  },
  market_value_eeg: {
    name: "market_value_eeg",
    type: "number",
    label: "Marktwert [ct/kWh]",
    helpText:
      "\nVergütung durch den Direktvermarkter für Strom, der ins öffentliche Netz eingespeist wird.\nAus der Differenz zwischen anzulegendem Wert und Marktwert ergibt sich die durch den\nNetzbetreiber ausgezahlte Marktprämie.\n\n\nWird das Feld nicht befüllt, wird davon ausgegangen, dass keine Direktvermarktung erfolgt\n(ab 100kWp ist die Direktvermarktung für PV-Anlagen jedoch verpflichtend).\n\n\n[Hier](https://www.netztransparenz.de/EEG/Marktpraemie/Marktwerte) können die Monatsmarktwerte\n(MW) wie auch historische Jahresmarktwerte (JW) als Orientierung herangezogen werden.\n",
    group: GENERATOR_EDIT_TABS.TAB_PRICE_DATA
  },
  direct_marketing_fee: {
    name: "direct_marketing_fee",
    type: "number",
    label: "Direktvermarktungsentgelt [ct/kWh]",
    helpText:
      "\nKosten, die für die Direktvermarktung anfallen. \n\n\nÜblicherweise fallen Kosten pro vermarkteter kWh an (ct/kWh). Als erster\nIndikator kann hier zwischen 0,4 – 0,6 ct/kWh angenommen werden. Je nach\nGröße und Anbieter der Vermarktung kann ebenfalls das Modell eines\nmonatlichen Entgelts (€/mtl.) zur Anwendung kommen.\n",
    group: GENERATOR_EDIT_TABS.TAB_PRICE_DATA
  },
  own_consumption_subsidy: {
    name: "own_consumption_subsidy",
    type: "number",
    label: "Eigenverbrauchszuschlag [ct/kWh]",
    helpText:
      "Für ältere KWK-Anlagen gemäß KWKG 2012 oder früheren Fassungen sowie für einige ältere PV-Anlagen wird auch für Strom, der durch den Anlagenbetreiber im Eigenverbrauch genutzt wird, eine gesetzliche Vergütung gemäß KWKG bzw. EEG gezahlt. Diese kann als Eigenverbrauchszuschlag berücksichtigt werden.",
    group: GENERATOR_EDIT_TABS.TAB_PRICE_DATA
  },
  kwkg_direct_delivery_subsidy: {
    name: "kwkg_direct_delivery_subsidy",
    type: "number",
    label: "KWK-Drittlieferzuschlag [ct/kWh]",
    helpText:
      "Für Strom aus KWK-Anlagen, der innerhalb einer Kundenanlage ohne Nutzung des öffentlichen Netzes an Dritte geliefert wird, wird u. U. ein Zuschlag gemäß KWKG gezahlt. Für Anlagen nach KWKG 2012 berechnet sich dieser Zuschlag gemäß § 6 Abs. 3 Nr. 2. i. V. m. § 7 Abs. 3 Nr. 2. Für KWK-Anlagen gemäß KWKG 2012 ist der Zuschlag identisch dem Zuschlag, der für eigenverbrauchten bzw. ins Netz eingespeisten Strom gezahlt wird.",
    group: GENERATOR_EDIT_TABS.TAB_PRICE_DATA
  },
  "einheit.im_mastr_registriert": {
    name: "einheit.im_mastr_registriert",
    type: "boolean",
    required: false,
    label: "Einheit wurde im Marktstammdatenregister gemeldet",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  "einheit.einheit_mastr_nummer": {
    name: "einheit.einheit_mastr_nummer",
    type: "text",
    required: false,
    label: "MaStR-Nummer der Einheit",
    maxLength: 15,
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  "einheit.name_stromerzeugungseinheit": {
    name: "einheit.name_stromerzeugungseinheit",
    type: "text",
    required: false,
    label: "Name der Einheit",
    helpText:
      "Entspricht bei Einheiten, die im Marktstammdatenregister registriert sind, dem dort hinterlegten Anzeigenamen.\n\nUm Einheiten auch liegenschaftsübergreifend eindeutig identifizieren zu können, vermeiden Sie die mehrmalige Verwendung derselben Bezeichnung für verschiedene Einheiten.",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  "einheit.datum_erstmalige_inbetriebnahme": {
    name: "einheit.datum_erstmalige_inbetriebnahme",
    type: "date",
    required: false,
    label: "Datum der erstmaligen Inbetriebnahme der Stromerzeugungseinheit",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  "einheit.einspeisungsart": {
    name: "einheit.einspeisungsart",
    type: "select",
    required: false,
    label: "Art der Einspeisung",
    helpText:
      "Angabe, ob der gesamte Strom aus der Stromerzeugungseinheit ins Netz eingespeist wird (Volleinspeisung) oder ob der Strom ganz oder teilweise in der Kundenanlage verbraucht wird (Teileinspeisung).\n\n*Volleinspeisung* ist die vollständige Einspeisung des erzeugten Stroms ins Stromnetz. Als Volleinspeisung gilt auch die sogenannte 'kaufmännisch-bilanzielle Weitergabe' des erzeugten Stroms an den Netzbetreiber.\n\n*Teileinspeisung* ist auszuwählen, wenn der erzeugte Strom teilweise oder vollständig in der Kundenanlage vom Betreiber selbst verbraucht wird (z. B. Eigenversorgung) oder ohne Nutzung des Netzes einem Dritten überlassen wird (direkte Stromlieferung).",
    data: [
      {
        value: "None",
        label: "---"
      },
      {
        value: "Volleinspeisung",
        label: "Volleinspeisung"
      },
      {
        value: "TeileinspeisungEigenverbrauch",
        label: "Teileinspeisung (Eigenverbrauch)"
      }
    ],
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  "einheit.typenbezeichnung": {
    name: "einheit.typenbezeichnung",
    type: "text",
    required: false,
    label: "Typenbezeichnung der Einheit",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  "einheit.seriennummer": {
    name: "einheit.seriennummer",
    type: "text",
    required: false,
    label: "Seriennummer der Stromerzeugungseinheit",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  "einheit.beschreibung": {
    name: "einheit.beschreibung",
    type: "textarea",
    required: false,
    label: "Beschreibung / Notizen",
    helpText:
      "In diesem Feld können Sie weiterführende Informationen bzw. Notizen festhalten. Diese dienen ausschließlich Ihrer Übersicht und werden durch die Software nicht verwendet.",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  "einheit.bruttoleistung": {
    name: "einheit.bruttoleistung",
    type: "number",
    required: true,
    label: "Bruttoleistung in kW",
    helpText: `Gemäß Definition aus dem Marktstammdatenregister ist die Bruttoleistung die an den Klemmen des 
Generators abgegebene elektrische Leistung in kW.\n\n
Beispiele:\n\n
- Bei BHKW kann regelmäßig die (Nenn-)Leistung auf dem Typenschild des Generators als Bruttoleistung 
eingetragen werden.
- Bei Solareinheiten gilt die Summe der Gleichstromleistung der Module in kWp als Bruttoleistung.
- Bei Windeinheiten wird im Marktstammdatenregister für die Bruttoleistung und die Nettonennleistung 
der gleiche Wert verwendet.`,
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },

  "einheit.zugeordnete_wirkleistung_wechselrichter": {
    name: "einheit.zugeordnete_wirkleistung_wechselrichter",
    type: "number",
    label: "Zugeordnete Wirkleistung des/der Wechselrichter",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },

  "einheit.technologie": {
    name: "einheit.technologie",
    type: "select",
    label: "Technologie",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL,
    helpText:
      "Technologie, die zur Stromerzeugung eingesetzt wird. Die möglichen Auswahlwerte entsprechen den Katalogwerten aus dem Marktstammdatenregister.",
    data: [
      {
        value: "None",
        label: "---"
      },
      {
        value: "Verbrennungsmotor",
        label: "Verbrennungsmotor"
      },
      {
        value: "Brennstoffzelle",
        label: "Brennstoffzelle"
      },
      {
        value: "Stirlingmotor",
        label: "Stirlingmotor"
      },
      {
        value: "Dampfmotor",
        label: "Dampfmotor"
      },
      {
        value: "ORCOrganicRankineCycleAnlage",
        label: "ORC (Organic Rankine Cycle)-Anlage"
      },
      {
        value: "GegendruckmaschinemitEntnahme",
        label: "Gegendruckmaschine mit Entnahme"
      },
      {
        value: "GegendruckmaschineohneEntnahme",
        label: "Gegendruckmaschine ohne Entnahme"
      },
      {
        value: "GasturbinenohneAbhitzekessel",
        label: "Gasturbinen ohne Abhitzekessel"
      },
      {
        value: "GasturbinenmitAbhitzekessel",
        label: "Gasturbinen mit Abhitzekessel"
      },
      {
        value: "GasturbinenmitnachgeschalteterDampfturbine",
        label: "Gasturbinen mit nachgeschalteter Dampfturbine"
      },
      {
        value: "KondensationsmaschinemitEntnahme",
        label: "Kondensationsmaschine mit Entnahme"
      },
      {
        value: "KondensationsmaschineohneEntnahme",
        label: "Kondensationsmaschine ohne Entnahme"
      },
      {
        value: "Sonstige",
        label: "Sonstige"
      },
      {
        value: "KalinaCycle",
        label: "Kalina-Cycle"
      }
    ]
  },

  "einheit.energietraeger": {
    name: "einheit.energietraeger",
    type: "select",
    label: "Energieträger",
    helpText:
      "Energieträger, der in der Stromerzeugungseinheit verwendet wird. Die möglichen Auswahlwerte entsprechen den Katalogwerten aus dem Marktstammdatenregister.\n\n Beispiel: Braunkohle oder Mineralölprodukte werden im MaStR als 'Energieträger' definiert, Braunkohlenbriketts bzw. Dieselkraftstoff als dazugehörige 'Brennstoffe'.",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL,
    data: [
      { value: "None", label: "---" },
      { value: "AndereGase", label: "andere Gase" },
      { value: "Biomasse", label: "Biomasse" },
      { value: "Braunkohle", label: "Braunkohle" },
      { value: "Erdgas", label: "Erdgas" },
      { value: "Geothermie", label: "Geothermie" },
      { value: "Grubengas", label: "Grubengas" },
      { value: "Kernenergie", label: "Kernenergie" },
      { value: "Klaerschlamm", label: "Klärschlamm" },
      { value: "Mineraloelprodukte", label: "Mineralölprodukte" },
      { value: "NichtBiogenerAbfall", label: "nicht biogener Abfall" },
      {
        value: "SolareStrahlungsenergie",
        label: "Solare Strahlungsenergie"
      },
      { value: "Solarthermie", label: "Solarthermie" },
      { value: "Speicher", label: "Speicher" },
      { value: "Steinkohle", label: "Steinkohle" },
      { value: "Waerme", label: "Wärme" },
      { value: "Wind", label: "Wind" },
      { value: "Wasser", label: "Wasser" },
      { value: "DruckAusGasleitungen", label: "Druck aus Gasleitungen" },
      {
        value: "DruckAusWasserleitungen",
        label: "Druck aus Wasserleitungen"
      },
      { value: "Wasserstoff", label: "Wasserstoff" }
    ]
  },

  // stromstg
  "anlage_stromstg.has_standortuebergreifende_verklammerung": {
    name: "anlage_stromstg.has_standortuebergreifende_verklammerung",
    type: "boolean",
    allowNull: true,
    required: false,
    label: "Für meine Anlage besteht eine standortübergreifende Verklammerung",
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    helpText:
      "Anlagen, die sich in unterschiedlichen Liegenschaften befinden, gelten unter bestimmten Bedingungen als eine stromsteuerliche Einheit. Wenn das für diese Anlage(n) der Fall ist, wählen Sie bitte _Ja_ aus.\n\nWeitere Informationen erhalten Sie durch das für Sie zuständige Hauptzollamt."
  },
  "anlage_stromstg.stfrei_paragraph_9_abs_1_nr_1_stromstg": {
    name: "anlage_stromstg.stfrei_paragraph_9_abs_1_nr_1_stromstg",
    type: "select",
    required: false,
    label:
      "Wurde für die Anlage vom Hauptzollamt eine Erlaubnis zum steuerfreien Selbstverbrauch gemäß § 9 Abs. 1 Nr. 1 StromStG (EE ab 2 MW) ausgestellt?*",
    helpText:
      "Wer Strom aus EE-Anlagen ab 2 MW stromsteuerfrei zum Selbstverbrauch entnehmen will, bedarf nach § 9 Abs. 4 S. 1 Nr. 1 StromStG einer Erlaubnis.\n\nDer Erlaubnisantrag muss beim zuständigen Hauptzollamt gestellt werden (Formblätter 1421, 1421a und 1421az).",
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    data: [
      { value: "yes", label: "Ja" },
      { value: "no", label: "Nein" }
    ]
  },
  "anlage_stromstg.datum_erlaubnis_stfrei_paragraph_9_abs_1_nr_1_stromstg": {
    name: "anlage_stromstg.datum_erlaubnis_stfrei_paragraph_9_abs_1_nr_1_stromstg",
    type: "date",
    required: false,
    label:
      "Datum, seit dem die Erlaubnis zum steuerfreien Selbstverbrauch gemäß § 9 Abs. 1 Nr. 1 StromStG besteht",
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    helpText:
      "Sofern der Antrag zur steuerfreien Entnahme nicht fristgerecht (für Neuanlagen vor Inbetriebnahme, für Bestandsanlagen bis 31.12.2019) gestellt wurde, besteht die Möglichkeit einer nachträglichen Steuerentlastung.\n\nHierfür ist im Regelfall zunächst eine Selbstversteuerung als Versorger bzw. Eigenerzeuger erforderlich (Amtlicher Vordruck 1400). Die Enlastung kann dann über das Formblatt 1471 beim Hauptzollamt beantragt werden.Die nachträgliche Entlastung ist bis zum 31.12. des Folgejahres möglich, danach in der Regel nicht mehr."
  },
  "anlage_stromstg.einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung": {
    name: "anlage_stromstg.einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung",
    type: "select",
    required: false,
    label:
      "Besteht für die Anlage eine förmliche Einzelerlaubnis nach § 9 Abs. 1 Nr. 2 StromStG zur steuerfreien Entnahme von Strom zur Stromerzeugung?*",
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    helpText:
      "Nach § 9 Abs. 1 Nr. 2 StromStG i.V.m. § 12 Abs. 1 StromStV wird Strom dann zur Stromerzeugung entnommen, wenn er in den Neben- und Hilfsanlagen einer Stromerzeugungseinheit insbesondere zur Wasseraufbereitung, Dampferzeugerwasserspeisung, Frischluftversorgung, Brennstoffversorgung und Rauchgasreinigung oder in Pumpspeicherkraftwerken von den Pumpen zum Fördern der Speichermedien zur Erzeugung von Strom im technischen Sinne verbraucht wird.\n\nEntlastungsberechtigt ist derjenige, der den Strom entnommen hat. Die Steuerbegünstigung kann entweder als Steuerbefreiung (förmliche Einzelerlaubnis für Versorger) nach § 9 Absatz 1 Nummer 2 i. V. m. Absatz 4 StromStG und § 8 und § 9 StromStV oder als Steuerentlastung nach § 9 Absatz 1 Nummer 2 StromStG i. V. m. § 12a StromStV gewährt werden.\n\nBei erstmaliger Antragstellung ist dem Antrag für jede Anlage eine Betriebserklärung nach amtlich vorgeschriebenem Vordruck beizufügen.\n\nNach § 9 Abs. 6 StromStG darf der erzeugte Strom nur zu dem in der Erlaubnis genannten Zweck steuerfrei entnommen werden.",
    data: [
      {
        value: "yes",
        label: "Ja"
      },
      {
        value: "no",
        label: "Nein"
      }
    ]
  },
  "anlage_stromstg.datum_einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung":
    {
      name: "anlage_stromstg.datum_einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung",
      type: "date",
      required: false,
      group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
      label:
        "Datum, seit dem die Einzelerlaubnis zur steuerfreien Entnahme von Strom zur Stromerzeugung nach § 9 Abs. 1 Nr. 2 StromStG besteht"
    },
  "anlage_stromstg.stromstg_paragraph_9_1_3a": {
    name: "anlage_stromstg.stromstg_paragraph_9_1_3a",
    type: "select",
    required: false,
    label:
      "Wurde für die Anlage vom Hauptzollamt eine Erlaubnis zum steuerfreien Selbstverbrauch im räumlichen Zusammenhang gemäß § 9 Abs. 1 Nr. 3 Buchstabe a StromStG (bis 2 MW) ausgestellt?*",
    helpText:
      "Wer Strom aus EE-Anlagen oder hocheffizienten KWK-Anlagen mit einer elektrischen Leistung bis 2 MW stromsteuerfrei zum Selbstverbrauch entnehmen will, bedarf nach § 9 Abs. 4 S. 1 Nr. 1 StromStG einer Erlaubnis.\n\nDer Erlaubnisantrag muss beim zuständigen Hauptzollamt gestellt werden (Formblätter 1422, 1422a und 1422az).",
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    data: [
      { value: "yes", label: "Ja" },
      { value: "no", label: "Nein" }
    ]
  },
  "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3a": {
    name: "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3a",
    type: "custom",
    label:
      "Zeitraum (Erlaubnis steuerfreier Selbstverbrauch räumlicher Zusammenhang §9 Abs.1 Nr.3a) (mind. Startdatum ist erforderlich)",
    required: false,
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    helpText:
      "Sofern der Antrag zur steuerfreien Entnahme nicht fristgerecht (für Neuanlagen vor Inbetriebnahme, für Bestandsanlagen bis 31.12.2019) gestellt wurde, besteht die Möglichkeit einer nachträglichen Steuerentlastung.\n\nHierfür ist im Regelfall zunächst eine Selbstversteuerung als Versorger bzw. Eigenerzeuger erforderlich (Amtlicher Vordruck 1400). Die Enlastung kann über die Formblätter 1470 (KWK) bzw. 1471 (EE) beim Hauptzollamt beantragt werden.Die nachträgliche Entlastung ist bis zum 31.12. des Folgejahres möglich, danach in der Regel nicht mehr."
  },
  "anlage_stromstg.stromstg_paragraph_9_1_3b": {
    name: "anlage_stromstg.stromstg_paragraph_9_1_3b",
    type: "select",
    required: false,
    label:
      "Wurde für die Anlage vom Hauptzollamt eine Erlaubnis zur steuerfreien Lieferung an Letztverbraucher, die den Strom im räumlichen Zusammenhang entnehmen, gemäß § 9 Abs. 1 Nr. 3 Buchstabe b StromStG (bis 2 MW) ausgestellt?*",
    helpText:
      "Wer Strom aus EE-Anlagen oder hocheffizienten KWK-Anlagen mit einer elektrischen Leistung bis 2 MW stromsteuerfrei an Letztverbraucher im räumlichen Zusammenhang leisten will, bedarf nach § 9 Abs. 4 S. 1 Nr. 1 StromStG einer Erlaubnis.\n\nDer Erlaubnisantrag muss beim zuständigen Hauptzollamt gestellt werden (Formblätter 1422, 1422a und 1422az).",
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    data: [
      { value: "yes", label: "Ja" },
      { value: "no", label: "Nein" }
    ]
  },
  "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3b": {
    name: "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3b",
    type: "custom",
    required: false,
    label:
      "Zeitraum (Erlaubnis steuerfreie Lieferung an Letztverbraucher räumlicher Zusammenhang §9 Abs.1 Nr.3b) (mind. Startdatum ist erforderlich)",
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    helpText:
      "Sofern der Antrag zur steuerfreien Entnahme nicht fristgerecht (für Neuanlagen vor Inbetriebnahme, für Bestandsanlagen bis 31.12.2019) gestellt wurde, besteht keine Möglichkeit einer nachträglichen Steuerentlastung."
  },
  "anlage_stromstg.hauptzollamt_anlagennummer": {
    name: "anlage_stromstg.hauptzollamt_anlagennummer",
    type: "text",
    required: false,
    label: "Anlagen-Nr. des Hauptzollamts (sofern vorhanden/bekannt)",
    group: GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
    helpText:
      "Die Nummer dient der Identifikation der Anlage gegenüber dem Hauptzollamt. Wenn Sie die Nummer hier eintragen, kann im Rahmen von Meldungen ans Hauptzollamt darauf zurückgegriffen werden."
  },
  pv_projector: {
    name: "pv_projector",
    type: "text",
    required: false,
    label: "Projektierer/Solarteur der PV-Anlage",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  pv_projector_contact: {
    name: "pv_projector_contact",
    type: "text",
    label: "Ansprechpartner beim Projektierer",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  pv_projector_contact_phone: {
    name: "pv_projector_contact_phone",
    type: "text",
    label: "Telefonnummer Ansprechpartner",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },
  pv_projector_contact_mail: {
    name: "pv_projector_contact_mail",
    type: "text",
    label: "E-Mail Ansprechpartner",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },

  "einheit.eeg_anlage.im_mastr_registriert": {
    name: "einheit.eeg_anlage.im_mastr_registriert",
    type: "boolean",
    label: "Einheit wurde im Marktstammdatenregister gemeldet",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA
  },
  "einheit.eeg_anlage.mastr_nummer": {
    name: "einheit.eeg_anlage.mastr_nummer",
    type: "text",
    label: "MaStR-Nummer der Anlage",
    // required: true, // todo remove when validation is tested enough
    maxLength: 15,
    placeholder: "z. B. EEG012345678910",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA
  },
  "einheit.eeg_anlage.foerderregime": {
    name: "einheit.eeg_anlage.foerderregime",
    type: "select",
    required: false,
    label: "Förderregime",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    helpText:
      "Bitte wählen Sie aus, ob bzw. über welche gesetzliche Förderung die Anlage vergütet wird. Diese Information ist u. a. relevant, um zu prüfen, welche regulatorischen Pflichten für die Anlage erfüllt werden müssen.",
    data: Object.values(FoerderRegime).map((foerderRegime) => ({
      value: foerderRegime,
      label: getFoerderregimeLabel(foerderRegime)
    }))
  },
  "einheit.eeg_anlage.jahr_des_gebotstermins": {
    name: "einheit.eeg_anlage.jahr_des_gebotstermins",
    type: "number",
    allowDecimal: false,
    thousandSeparator: false,
    required: true,
    label: "Jahr des Gebotstermins",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    helpText:
      "Die Angabe des Gebotstermins im Zusammenhang mit der Inbetriebnahme ist ausschlaggebend für die korrekte Ermittlung der Marktprämie (z. B. des Jahresmarktwerts) sowie der Stundenregel in Zeiträumen negativer Strompreise gemäß § 51 EEG.\n\n\nDas Jahr des Gebotstermins wird aus der Zuschlagsnummer Ihrer im Marktstammdatenregister hinterlegten Daten ermittelt, wenn Sie dieses Feld dort hinterlegt haben.",
    placeholder:
      "Bitte tragen Sie das Jahr des Gebotstermins im Format JJJJ ein."
  },
  "einheit.eeg_anlage.gebotstermin": {
    name: "einheit.eeg_anlage.gebotstermin",
    type: "date",
    required: true,
    minDate: DateTime.fromISO("2025-01-01"),
    maxDate: DateTime.fromISO("2025-12-31"),
    label: "Gebotstermin",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    placeholder: "Bitte wählen Sie den exakten Gebotstermin im Jahr 2025."
  },
  "einheit.eeg_anlage.anlagenschluessel_eeg": {
    name: "einheit.eeg_anlage.anlagenschluessel_eeg",
    type: "text",
    label: "Anlagenschlüssel EEG",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA
  },
  "einheit.eeg_anlage.eeg_inbetriebnahmedatum": {
    name: "einheit.eeg_anlage.eeg_inbetriebnahmedatum",
    type: "date",
    label: "EEG-Inbetriebnahmedatum",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA
  },
  "einheit.eeg_anlage.installierte_leistung": {
    name: "einheit.eeg_anlage.installierte_leistung",
    type: "number",
    label: "Installierte Leistung der EEG-Anlage in kW",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA
  },
  "einheit.eeg_anlage.paragraph_24_zusammenfassung.custom_generator_ids": {
    name: "einheit.eeg_anlage.paragraph_24_zusammenfassung.custom_generator_ids",
    type: "custom",
    label: "Zusammengefasste Anlage(n) gemäß §\u00A024 EEG",
    infoText:
      'Aktivieren Sie die "Manuelle Zuordnung", um die systemseitig vorgenommene Zusammenfassung anzupassen. Damit wird die automatisierte Zusammenfassung ausgesetzt. Eine Änderung der Zuordnung kann Auswirkung auf Berechnungen im Rahmen von § 6 EEG sowie Erlösmonitoring haben. Bitte prüfen Sie Ihre Angaben sorgfältig.'
  },
  "einheit.eeg_anlage.anzulegender_wert": {
    name: "einheit.eeg_anlage.anzulegender_wert",
    alias: "anzulegende_werte",
    type: "custom",
    label: "Anzulegender Wert in ct/kWh",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA
  },
  "einheit.eeg_anlage.veraeusserungsformen": {
    name: "einheit.eeg_anlage.veraeusserungsformen",
    type: "custom",
    label: "Veräußerungsformen",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA
  },
  "mieterstromzuschlag.application_intent": {
    name: "mieterstromzuschlag.application_intent",
    type: "radio",
    label: MIETERSTROMZUSCHLAG_FORM.APPLICATION_INTENT_LABEL,
    helpText: MIETERSTROMZUSCHLAG_FORM.APPLICATION_INTENT_HELP_TEXT,
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    choices: [
      {
        value: MieterstromzuschlagApplicationIntent.ApplyOrAlreadyApplied,
        label: MIETERSTROMZUSCHLAG_FORM.APPLY_OR_ALREADY_APPLIED_LABEL
      },
      {
        value: MieterstromzuschlagApplicationIntent.DoNotApply,
        label: MIETERSTROMZUSCHLAG_FORM.DO_NOT_APPLY_LABEL
      }
    ] satisfies Array<RadioProps>
  },
  "mieterstromzuschlag.application_date": {
    name: "mieterstromzuschlag.application_date",
    type: "date",
    required: false,
    label: MIETERSTROMZUSCHLAG_FORM.APPLICATION_DATE_LABEL,
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA
  },

  // kwk
  "einheit.kwk_anlage.mastr_nummer": {
    name: "einheit.kwk_anlage.mastr_nummer",
    type: "text",
    required: false,
    label: "MaStR-Nummer der Anlage",
    placeholder: "z. B. EEG012345678910",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA
  },
  "einheit.kwk_anlage.im_mastr_registriert": {
    name: "einheit.kwk_anlage.im_mastr_registriert",
    type: "boolean",
    required: false,
    label: "Anlage wurde im Marktstammdatenregister gemeldet",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA
  },
  "einheit.kwk_anlage.elektrische_kwk_leistung": {
    name: "einheit.kwk_anlage.elektrische_kwk_leistung",
    type: "number",
    required: false,
    label: "Elektrische KWK Leistung",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA
  },
  "einheit.kwk_anlage.inbetriebnahmedatum": {
    name: "einheit.kwk_anlage.inbetriebnahmedatum",
    type: "date",
    required: false,
    label: "KWK-Inbetriebnahmedatum",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA
  },
  "einheit.kwk_anlage.foerderregime": {
    name: "einheit.kwk_anlage.foerderregime",
    type: "select",
    required: false,
    label: "Förderregime",
    helpText:
      "Bitte wählen Sie aus, ob bzw. über welche gesetzliche Förderung die Anlage vergütet wird. Diese Information ist u. a. relevant, um zu prüfen, welche regulatorischen Pflichten für die Anlage erfüllt werden müssen.",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
    data: [
      {
        value: "keine_foerderung_in_anspruch_genommen",
        label: "Keine gesetzliche Förderung / Zuschläge in Anspruch genommen"
      },
      {
        value: "foerderung_ausgelaufen",
        label: "Förderung ausgelaufen"
      },
      {
        value: "eeg_fixe_einspeiseverguetung",
        label: "EEG fixe Einspeisevergütung"
      },
      {
        value: "eeg_ausschreibung",
        label: "EEG Marktprämie - über Ausschreibung ermittelt"
      },
      {
        value: "eeg_ohne_ausschreibung",
        label: "EEG Marktprämie - gesetzlich festgelegt (ohne Ausschreibung)"
      },
      {
        value: "eeg_innovationsausschreibung",
        label:
          "EEG (fixe) Marktprämie - über Innovationsausschreibung ermittelt"
      },
      {
        value: "kwkg_zuschlag_2016_2020",
        label: "Zuschlag gemäß KWKG 2016 oder KWKG 2020 (ohne Ausschreibung)"
      },
      {
        value: "kwkg_zuschlag_2012",
        label: "Zuschlag gemäß KWKG 2012"
      },
      {
        value: "kwkg_ausschreibung",
        label: "Zuschlag KWKG über Ausschreibung"
      },
      {
        value: "kwkg_zuschlag_2000_2002_2009",
        label: "Zuschlag gemäß KWKG 2000, 2002 oder 2009"
      }
    ]
  },
  "einheit.kwk_anlage.bafa_anlagennummer": {
    name: "einheit.kwk_anlage.bafa_anlagennummer",
    type: "text",
    required: false,
    label: "BAFA-Anlagennummer",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
    helpText:
      "Die BAFA-Anlagennummer dient der Identifikation der Anlage gegenüber dem BAFA. Wenn Sie die Nummer hier eintragen, kann im Rahmen von Meldungen an das BAFA darauf zurückgegriffen werden."
  },
  "einheit.kwk_anlage.energiesteuerentlastung_paragraph_53_energiestg": {
    name: "einheit.kwk_anlage.energiesteuerentlastung_paragraph_53_energiestg",
    type: "select",
    required: false,
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
    label:
      "Soll eine Energiesteuerentlastung nach § 53 EnergieStG für die Anlage in Anspruch genommen werden (für KWK-Anlagen bis 2 MW regelmäßig nein)?",
    helpText:
      "Für KWK-Anlage bis 2 MW hat dieser Entlastungstatbestand meist keine Relevanz, da i. d. R. die Energiesteuer-Entlastung für KWK nach § 53a EnergieStG zur Anwendung kommt.\n\nHinsichtlich der Entlastung nach § 53 gilt: Antragsberechtigt ist derjenige, der die Energieerzeugnisse in Anlagen zur Stromerzeugung mit einer elektrischen Nennleistung von mehr als 2 MW verwendet hat. Sind die Energieerzeugnisse in einer Anlage mit einer Nennleistung von bis zu 2 MW verwendet worden, kann ein Antrag auf Steuerentlastung nach § 53 EnergieStG gestellt werden, soweit der erzeugte Strom nicht nach § 9 Abs. 1 Nr. 1, 3, 4, 5 oder 6 des Stromsteuergesetzes von der Stromsteuer befreit ist.\n\nAus unionsrechtlichen Gründen sollen entweder der erzeugte Strom oder aber die zur Erzeugung von Strom verwendeten Energieerzeugnisse besteuert werden. Dieser Grundsatz gilt jedoch nicht, wenn der erzeugte Strom wiederum zur Aufrechterhaltung der Fähigkeit, elektrischen Strom zu erzeugen, entnommen wird. Basierend darauf kann neben einer Steuerentlastung nach § 53 EnergieStG für zur Stromerzeugung eingesetzte Energieerzeugnisse parallel auch eine Steuerbefreiung für Strom zur Stromerzeugung nach § 9 Abs. 1 Nr. 2 StromStG vorliegen.\n\nWird die erzeugte mechanische Energie auch zu anderen Zwecken als zur Stromerzeugung in ortsfesten Anlagen verwendet, wird nur für die auf die Stromerzeugung entfallenden Energieerzeugnisse eine Steuerentlastung gewährt. Energieerzeugnisse gelten nur dann als zur Stromerzeugung verwendet, wenn sie in der Stromerzeugungsanlage unmittelbar am Energieumwandlungsprozess teilnehmen.",
    data: [
      {
        value: "yes",
        label: "Ja"
      },
      {
        value: "no",
        label: "Nein"
      }
    ]
  },
  "einheit.kwk_anlage.energiesteuerentlastung_paragraph_53a_energiestg": {
    name: "einheit.kwk_anlage.energiesteuerentlastung_paragraph_53a_energiestg",
    type: "select",
    required: false,
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
    label:
      "Soll eine Energiesteuerentlastung nach § 53a EnergieStG für die Anlage in Anspruch genommen werden (für KWK-Anlagen bis 2 MW regelmäßig ja)?",
    helpText:
      "Eine vollständige Steuerentlastung nach § 53a Abs. (6) EnergieStG wird gewährt für nachweislich versteuerte Energieerzeugnisse, die zur gekoppelten Erzeugung von Kraft und Wärme in ortsfesten Anlagen verwendet worden sind, wenn diese Anlagen einen Monats- oder Jahresnutzungsgrad von mindestens 70 Prozent erreichen und hocheffizient sind.\n\nDie vollständige Steuerentlastung wird nur gewährt bis zur vollständigen Absetzung für die Abnutzung der Hauptbestandteile der Anlage entsprechend den Vorgaben des § 7 des Einkommensteuergesetzes. Hauptbestandteile der Anlage sind Gasturbine, Motor, Dampferzeuger, Dampfturbine, Generator und Steuerung. Werden Hauptbestandteile der Anlage durch neue Hauptbestandteile ersetzt, wird die Steuerentlastung bis zur vollständigen Absetzung für Abnutzung der neu eingefügten Hauptbestandteile gewährt, sofern die Kosten für die Erneuerung mindestens 50 Prozent der Kosten für die Neuerrichtung der Anlage betragen. Die vollständige Steuerentlastung nach Absatz 6 erfolgt abzüglich der erhaltenen Investitionsbeihilfen. Solange die Investitionsbeihilfen den Steuerentlastungsbetrag nach § 53a erreichen oder übersteigen, wird die Steuerentlastung nicht gewährt. Der Entlastungsberechtigte nach Absatz 10 Satz 1 ist verpflichtet, dem zuständigen Hauptzollamt Angaben zu sämtlichen Investitionsbeihilfen zu machen, die ihm gewährt werden.\n\nEine teilweise Steuerentlastung nach § 53a Abs. (1) bzw. Abs. (4) wird gewährt für nachweislich versteuerte Energieerzeugnisse, die zur gekoppelten Erzeugung von Kraft und Wärme in ortsfesten Anlagen bzw. zum Antrieb von Gasturbinen und Verbrennungsmotoren in nach § 3 EnergieStG begünstigten Anlagen mit einem Monats- oder Jahresnutzungsgrad von mindestens 70 Prozent verheizt worden sind.",
    data: [
      {
        value: "keine_steuerentlastung",
        label: "Keine Steuerentlastung"
      },
      {
        value: "teilweise_steuerentlastung",
        label: "teilweise Steuerentlasung nach § 53a Abs. (4) EnergieStG"
      }
    ]
  },
  "einheit.kwk_anlage.steuerbefreiung_strom_zur_stromerz_paragraph_9_abs_2_stromstg":
    {
      name: "einheit.kwk_anlage.steuerbefreiung_strom_zur_stromerz_paragraph_9_abs_2_stromstg",
      type: "select",
      required: false,
      label:
        "Soll für die Anlage von der Steuerbefreiung nach § 9 Abs. 2 StromStG (Strom zur Stromerzeugung) Gebrauch gemacht werden?",
      group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
      helpText:
        "Nach § 9 Abs. 1 Nr. 2 StromStG i.V.m. § 12 Abs. 1 StromStV wird Strom dann zur Stromerzeugung entnommen, wenn er in den Neben- und Hilfsanlagen einer Stromerzeugungseinheit insbesondere zur Wasseraufbereitung, Dampferzeugerwasserspeisung, Frischluftversorgung, Brennstoffversorgung und Rauchgasreinigung oder in Pumpspeicherkraftwerken von den Pumpen zum Fördern der Speichermedien zur Erzeugung von Strom im technischen Sinne verbraucht wird.\n\nEntlastungsberechtigt ist derjenige, der den Strom entnommen hat. Die Steuerbegünstigung kann entweder als Steuerbefreiung (förmliche Einzelerlaubnis für Versorger) nach § 9 Absatz 1 Nummer 2 i. V. m. Absatz 4 StromStG und § 8 und § 9 StromStV oder als Steuerentlastung nach § 9 Absatz 1 Nummer 2 StromStG i. V. m. § 12a StromStV gewährt werden.\n\nBei erstmaliger Antragstellung ist dem Antrag für jede Anlage eine Betriebserklärung nach amtlich vorgeschriebenem Vordruck beizufügen.\n\nNach § 9 Abs. 6 StromStG darf der erzeugte Strom nur zu dem in der Erlaubnis genannten Zweck steuerfrei entnommen werden.",
      data: [
        {
          value: "yes",
          label: "Ja"
        },
        {
          value: "no",
          label: "Nein"
        }
      ]
    },
  "einheit.kwk_anlage.gesamtwirkungsgrad": {
    name: "einheit.kwk_anlage.gesamtwirkungsgrad",
    type: "number",
    required: false,
    label: "Gesamtwirkungsgrad der Anlage [%]",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
    min: 0,
    max: 100
  },
  "einheit.kwk_anlage.hocheffizient": {
    name: "einheit.kwk_anlage.hocheffizient",
    type: "select",
    required: false,
    label: "Anlage gilt als hocheffizient",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
    helpText:
      "Zur Inanspruchnahme einer Förderung gemäß KWKG sowie verschiedener energierechtlicher Privilegierungen muss eine KWK-Anlage als hocheffizient gelten. Für KWK-Anlagen bis 2 MW kann regelmäßig der Zulassungsbescheid des BAFA als Nachweis der Hocheffizienz genutzt werden. Für Anlagen ab 2 MW erfolgt der Nachweis i. d. R. über ein Sachverständigengutachten.",
    data: [
      {
        value: "yes",
        label: "Ja"
      },
      {
        value: "no",
        label: "Nein"
      }
    ]
  },
  "einheit.kwk_anlage.mech_energie_stromerzeugung_paragraph_3_abs_1_nr_1_energiestg":
    {
      name: "einheit.kwk_anlage.mech_energie_stromerzeugung_paragraph_3_abs_1_nr_1_energiestg",
      type: "select",
      required: false,
      group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
      label:
        "Dient die mechanische Energie der Anlage ausschließlich der Stromerzeugung (Anlage gemäß § 3 Abs. 1 Nr. 1 EnergieStG)?",
      helpText:
        "In den allermeisten Stromerzeugungsanlagen (inkl. KWK / BHKW) dient die mechanische Energie ausschließlich der Stromerzeugung. Ausnahmen, in denen dies nicht gegebeben ist, sind Gasklimageräte (auch unter dem Begriff Gaswärmepumpen bekannt), Trocknungsanlagen oder spezielle Häcksler.",
      data: [
        {
          value: "yes",
          label: "Ja"
        },
        {
          value: "no",
          label: "Nein"
        }
      ]
    },
  "einheit.kwk_anlage.nutzungsgrad": {
    name: "einheit.kwk_anlage.nutzungsgrad",
    type: "number",
    required: false,
    label: "Nutzungsgrad der Anlage [%]",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
    min: 0,
    max: 100
  },
  "einheit.kwk_anlage.vorrichtungen_abwaermeabfuhr": {
    name: "einheit.kwk_anlage.vorrichtungen_abwaermeabfuhr",
    type: "select",
    required: false,
    label: "Vorrichtungen zur Abwärmeabfuhr vorhanden",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
    helpText:
      "Sofern KWK-Anlagen über eine Vorrichtung zur Abwärmeabfuhr verfügen, ist eine monatliche Datenmeldung an das BAFA und den Anschlussnetzbetreiber erforderlich. Für Anlagen bis 2 MW ohne entsprechende Vorrichtung ist die jährliche Meldung ausreichend. Für KWK-Anlagen, die ausschließlich wärmegeführt betrieben werden und weder über einen Notkühler noch über einen Bypass zur Umgehung des Abgaswärmetauschers verfügen, kann der Nutzungsgrad im Rahmen der Energiesteuerentlasung den technischen Beschreibungen entnommen werden (entspricht dem Wirkungsgrad im Datenblatt). In diesem Fall kann ist eine separate Nutzungsgradberechnung nicht erforderlich. ",
    data: [
      {
        value: "yes",
        label: "Ja"
      },
      {
        value: "no",
        label: "Nein"
      }
    ]
  },
  "einheit.kwk_anlage.vollbenutzungsstunden": {
    name: "einheit.kwk_anlage.vollbenutzungsstunden",
    type: "custom",
    required: true,
    label: "Vollbenutzungsstunden",
    group: GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA
  },
  "einheit.tr_id": {
    name: "einheit.tr_id",
    type: "text",
    label: "TR-ID der Anlage",
    placeholder: "z. B. D0987654321",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },

  // regulatory data
  initial_installed_capacity: {
    name: "initial_installed_capacity",
    type: "number",
    required: false,
    label: "Ursprüngliche installierte Leistung [kW]",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    hiddenIfNull: true
  },
  building_installed: {
    name: "building_installed",
    type: "select",
    required: false,
    label: "Auf, an oder in einem Gebäude installiert, nach §48 (3) EEG",
    helpText:
      "Die Anforderungen aus §48 (3) EEG sind im Allgemeinen erfüllt, wenn es sich um ein Wohngebäude handelt oder das Gebäude nicht im Außenbereich nach §35 des Baugesetzbuchs errichtet ist. In anderen Fällen ist eine Einzelfallprüfung erforderlich.",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    data: [
      {
        value: "yes",
        label: "Ja"
      },
      {
        value: "no",
        label: "Nein"
      },
      {
        value: "unknown",
        label: "Unbekannt"
      }
    ],
    hiddenIfNull: true
  },
  residential_building_installed: {
    name: "residential_building_installed",
    type: "select",
    required: false,
    label: "Auf, an oder in einem Wohngebäude installiert",
    helpText:
      "Ein Gebäude gilt als Wohngebäude, wenn mindestens 40 Prozent der Fläche dem Wohnen dienen. Dies schließt Alten-, Pflege-, Wohnheime und vergleichbare Einrichtungen ein.",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    data: [
      {
        value: "yes",
        label: "Ja"
      },
      {
        value: "no",
        label: "Nein"
      },
      {
        value: "unknown",
        label: "Unbekannt"
      }
    ],
    hiddenIfNull: true
  },
  eligible_for_non_building_installed_feedin_tariff: {
    name: "eligible_for_non_building_installed_feedin_tariff",
    type: "select",
    required: false,
    label: "Berechtigt eine Vergütung nach §48 (1) EEG zu erhalten",
    helpText:
      "Die Anforderungen aus §48 (1) EEG werden meist erfüllt bei Anlagen in einem Korridor von 110m entlang von Autobahnen oder Schienenwegen, auf Versiegelungs- oder Konversionsflächen oder in Bereichen, in welchen ein Bau von Photovoltaikanlagen bereits nach dem Bebauungsplan von 2003 zulässig ist.",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    data: [
      {
        value: "yes",
        label: "Ja"
      },
      {
        value: "no",
        label: "Nein"
      },
      {
        value: "unknown",
        label: "Unbekannt"
      }
    ],
    hiddenIfNull: true
  },
  registered_for_mieterstrom: {
    name: "registered_for_mieterstrom",
    type: "select",
    required: false,
    label: "Für Mieterstrom registriert",
    helpText:
      "Ist die Anlage der Veräußerungsform Mieterstromzuschlag nach §21 Abs. 1 Nr. 3 zugeordnet?",
    group: GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
    data: [
      {
        value: "yes",
        label: "Ja"
      },
      {
        value: "no",
        label: "Nein"
      },
      {
        value: "unknown",
        label: "Unbekannt"
      }
    ],
    hiddenIfNull: true
  },

  // emsys info
  "einheit.sr_code": {
    name: "einheit.sr_code",
    type: "text",
    label: "SR-ID",
    placeholder: "z. B. C1234567890",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL
  },

  is_redispatch_import_active: {
    name: "is_redispatch_import_active",
    label: "Redispatch/DVA Import",
    type: "boolean"
  },

  "emsys.is_vollmacht_provided": {
    name: "emsys.is_vollmacht_provided",
    type: "boolean",
    label: "Vollmacht für Redispatch/DVA Import erteilt"
  },
  "emsys.date_emsys_onboarding_start": {
    name: "emsys.date_emsys_onboarding_start",
    type: "date",
    label: "Startdatum Emsys-Onboarding"
  }
} as const satisfies FormFieldData<GeneratorSingleEndpointResponse>;

const BIOMASSE_HAUPTBRENNSTOFF_CHOICES = {
  "einheit.hauptbrennstoff": {
    name: "einheit.hauptbrennstoff",
    type: "select",
    required: false,
    label: "Hauptbrennstoff (Biomasse)",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL,
    helpText:
      "Brennstoff, der in der Stromerzeugungseinheit verwendet wird. Die möglichen Auswahlwerte entsprechen den Katalogwerten aus dem Marktstammdatenregister.\n\nBeispiel: Braunkohle oder Mineralölprodukte werden im MaStR als 'Energieträger' definiert, Braunkohlenbriketts bzw. Dieselkraftstoff als dazugehörige 'Brennstoffe'.",
    data: [
      {
        value: "None",
        label: "---"
      },
      {
        value: "FesteBiogeneStoffeUndAbfaelleOhneHolz",
        label: "Feste biogene Stoffe und Abfälle (ohne Holz)"
      },
      {
        value: "FluessigeBiogeneStoffeUndAbfaelle",
        label: "Flüssige biogene Stoffe und Abfälle"
      },
      {
        value: "AltholzGebrauchtholzHolzmuell",
        label: "Altholz, Gebrauchtholz, Holz(sperr)müll"
      },
      {
        value: "Biodiesel",
        label: "Biodiesel"
      },
      {
        value: "Biogas",
        label: "Biogas"
      },
      {
        value: "BiomethanBioerdgas",
        label: "Biomethan (Bioerdgas)"
      },
      {
        value: "Biomethanol",
        label: "Biomethanol"
      },
      {
        value: "TierUndBlutmehl",
        label: "Tier- und Blutmehl"
      },
      {
        value: "BrennlaugeSchwarzlaugeSulfitablauge",
        label: "Brennlauge, Schwarzlauge, Sulfitablauge"
      },
      {
        value: "Deponiegas",
        label: "Deponiegas"
      },
      {
        value: "FesteBiogeneStoffe",
        label: "Feste biogene Stoffe"
      },
      {
        value: "Harzoel",
        label: "Harzöl"
      },
      {
        value: "WaldHolzhackschnitzelWaldScheitholzWaldKronenholz",
        label: "Wald-Holzhackschnitzel, Wald-Scheitholz, -Kronenholz"
      },
      {
        value: "Holzreste",
        label: "Holzreste (z. B. aus Schreinereien, auch Spanholz)"
      },
      {
        value: "HolzspaeneSaegemehl",
        label: "Holzspäne, Sägemehl"
      },
      {
        value: "Klaergas",
        label: "Klärgas"
      },
      {
        value: "RindeLandschaftspflegeholz",
        label: "Rinde und Landschaftspflegeholz"
      },
      {
        value: "PalmoelPflanzenoel",
        label: "Palmöl u.a. Pflanzenöle"
      },
      {
        value: "PelletsHolz",
        label: "Holz-Pellets, Holz-Briketts"
      },
      {
        value: "SchleifstaubBiogen",
        label: "Schleifstaub, biogen"
      },
      {
        value: "StrohStrohpellets",
        label: "Stroh, Strohpellets"
      },
      {
        value: "Terpentin",
        label: "Terpentin"
      },
      {
        value: "Tierfett",
        label: "Tierfett"
      },
      {
        value: "HolzgasGasBiomasse",
        label: "Holzgas (Gas Biomasse)"
      },
      {
        value: "Holzkohle",
        label: "Holzkohle"
      },
      {
        value: "WaldStammholzRundholz",
        label: "Wald-Stammholz, Rundholz"
      },
      {
        value: "EnergiepflanzenZurVerbrennung",
        label: "Energiepflanzen zur Verbrennung (z. B. Kurzumtriebs-Holz)"
      }
    ]
  }
} as const satisfies FormFieldData<GeneratorSingleEndpointResponse>;

const VERBRENNUNG_HAUPTBRENNSTOFF_CHOICES = {
  "einheit.hauptbrennstoff": {
    name: "einheit.hauptbrennstoff",
    type: "select",
    required: false,
    label: "Hauptbrennstoff (Biomasse)",
    group: GENERATOR_EDIT_TABS.TAB_GENERAL,
    helpText:
      "Brennstoff, der in der Stromerzeugungseinheit verwendet wird. Die möglichen Auswahlwerte entsprechen den Katalogwerten aus dem Marktstammdatenregister.\n\nBeispiel: Braunkohle oder Mineralölprodukte werden im MaStR als 'Energieträger' definiert, Braunkohlenbriketts bzw. Dieselkraftstoff als dazugehörige 'Brennstoffe'.",
    data: [
      { value: "None", label: "---" },
      { value: "KohlenwertstoffeAusSteinkohle", label: "Steinkohle" },
      { value: "Steinkohlen", label: "Steinkohlen" },
      { value: "Steinkohlenbriketts", label: "Steinkohlenbriketts" },
      { value: "Steinkohlenkoks", label: "Steinkohlenkoks" },
      { value: "Braunkohlenbriketts", label: "Braunkohlenbriketts" },
      { value: "Braunkohlenkoks", label: "Braunkohlenkoks" },
      { value: "Hartbraunkohlen", label: "Hartbraunkohlen" },
      { value: "Rohbraunkohlen", label: "Rohbraunkohlen" },
      { value: "StaubUndTrockenkohle", label: "Trockenkohle" },
      { value: "Wirbelschichtkohle", label: "Wirbelschichtkohle" },
      { value: "Dieselkraftstoff", label: "Dieselkraftstoff" },
      { value: "HeizoelLeicht", label: "leicht" },
      { value: "HeizoelSchwer", label: "schwer" },
      { value: "Fluessiggas", label: "Flüssiggas" },
      { value: "Petrolkoks", label: "Petrolkoks" },
      { value: "Raffineriegas", label: "Raffineriegas" },
      { value: "AndereMineraloelprodukte", label: "Mineralölprodukte" },
      { value: "ErdgasErdoelgas", label: "Erdölgas" },
      { value: "GrubengasVerbrennung", label: "Grubengas" },
      { value: "HochofengasKonvertergas", label: "Konvertergas" },
      { value: "Kokereigas", label: "Kokereigas" },
      { value: "AndereGase", label: "Gase" },
      { value: "SonstigeHergestellteGase", label: "Gase" },
      { value: "Industrieabfall", label: "Industrieabfall" },
      { value: "AbfallHausmuellSiedlungsabfaelle", label: ".)" },
      { value: "Dampf", label: ")" },
      { value: "Waerme", label: "Wärme" },
      { value: "Wasserstoff", label: "Wasserstoff" }
    ]
  }
} as const satisfies FormFieldData<GeneratorSingleEndpointResponse>;

export const paragraph_24_zusammenfassung_helpText = `Informationen zur "Verklammerung" nach §\u00A024 EEG: Mehrere Anlagen sind unabhängig von den Eigentumsverhältnissen zum Zweck der Ermittlung des Anspruchs nach § 19 Absatz 1 und zur Bestimmung der Größe der Anlage nach § 21 Absatz 1 oder § 22 für den jeweils zuletzt in Betrieb gesetzten Generator als eine Anlage anzusehen, wenn
1. sie sich auf demselben Grundstück, demselben Gebäude, demselben Betriebsgelände oder sonst in unmittelbarer räumlicher Nähe befinden,
2. sie Strom aus gleichartigen erneuerbaren Energien erzeugen,
3. für den in ihnen erzeugten Strom der Anspruch nach § 19 Absatz 1 in Abhängigkeit von der Bemessungsleistung oder der installierten Leistung besteht und
4. sie innerhalb von zwölf aufeinanderfolgenden Kalendermonaten in Betrieb genommen worden sind.`;

export {
  BIOMASSE_HAUPTBRENNSTOFF_CHOICES,
  GENERATOR_FORM_DATA,
  VERBRENNUNG_HAUPTBRENNSTOFF_CHOICES
};
